<template>
  <div class="fulfillment-details">
    <Loading v-if="slideOutLoading" />
    <ShipIt
      v-model="selectedOption"
      :product-id="productInfo.productId"
      :bag-item-id="productInfo.bagItemId"
      :product-availability-info="productInfo.ship"
      :store-details="productInfo.shipFromStore"
      :zip-code="shipZipCode"
      :is-international-ship="isInternationalShip"
      :page="page.toLowerCase()"
      :is-e-gift-card="productInfo.isEGiftCard"
      :e-gift-card-info="productInfo.eGiftCardInfo"
      :is-parcel-shipping="isParcelShipping"
      :is-furniture-shipping="isFurnitureShipping"
      :pickup-store-details="pickupStore"
      :update-ship-zip-code-callback="updateShipZipCodeCallback"
      :free-shipping-eligible="freeShippingEligible"
      :copy-clarity-vue-enabled="copyClarityVueEnabled"
      :ship-event-data="shipEventData"
      :sdd-event-data="sddNddEventData"
      :delivery-days="productInfo.deliveryDays"
      :product-category-id="productCategoryId"
      @option-change="automaticOptionChange"
      @updated:zipCode="onUpdateZipcode"
      @toggleLoaderForSlideout="toggleLoaderForSlideout"
    />
    <ul class="delivery-days hide">
      <li
        v-for="(value, key) in productInfo.deliveryDays"
        :key="key"
        :class="key"
      >
        {{ value }}
      </li>
    </ul>
    <Pickup
      v-if="isPickupShown"
      v-model="selectedOption"
      :product-id="productInfo.productId"
      :bag-item-id="productInfo.bagItemId"
      :store-details="pickupStore"
      :product-availability-info="productInfo.pickup"
      :sdd-eligibility-flag="
        productInfo.ship.sddAvailability && productInfo.ship.available
      "
      :boss-enabled="bossEnabled"
      :page="page.toLowerCase()"
      :bag-items="bagItems"
      :show-accordion="showAccordion"
      :upc-number="upcNumber"
      :fcc-macys-api-key="fccMacysApiKey"
      :pickup-event-data="pickupEventData"
      :delivery-days="productInfo.deliveryDays"
      :product-category-id="productCategoryId"
      @option-change="automaticOptionChange"
      @update-radio-button="selectPickupOnStoreChange"
      @updated:storeDetails="onUpdateStore"
      @toggleLoaderForSlideout="toggleLoaderForSlideout"
    />
  </div>
</template>

<script>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { fireTag } from '@vue-util/tagManager';
import Loading from '../loading/Loading'; // TODO: once this component is available in atomic-ui(TEC-2316), start importing from there.
import getPickupAvailability from '../../utils/getPickupAvailability';
import bopsAvailabilityTag from '../../utils/bopsAvailabilityTag';
import Pickup from './Pickup';
import ShipIt from './ShipIt';

export default {
  name: 'Fulfillment',
  components: {
    Pickup,
    ShipIt,
    Loading,
  },
  props: {
    pickupStore: {
      type: Object,
      required: true,
    },
    productInfo: {
      type: Object,
      required: true,
    },
    shipZipCode: {
      type: String,
      default: '',
    },
    bossEnabled: {
      type: Boolean,
      default: true,
    },
    isInternationalShip: {
      type: Boolean,
    },
    page: {
      type: String,
      default: '',
    },
    bagItems: {
      type: Array,
      default: () => [],
    },
    showAccordion: {
      type: Boolean,
    },
    upcNumber: {
      type: String,
      default: '',
    },
    fccMacysApiKey: {
      type: String,
      default: '',
    },
    updateShipZipCodeCallback: {
      type: Function,
    },
    freeShippingEligible: {
      type: Boolean,
      default: false,
    },
    copyClarityVueEnabled: {
      type: Boolean,
      default: false,
    },
    eventBadging: {
      type: Object,
      default: null,
    },
    productCategoryId: {
      type: String,
      required: true,
    },
    sddPromoEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selected-option', 'current:zipCode', 'current:storeDetails'],
  setup(props, { emit }) {
    const slideOutLoading = ref(false);
    const selectedOption = ref(props.productInfo.selectedRadioButton);
    const currentStoreDetails = ref(props.pickupStore);
    const currentZipCode = { currentZipCode: ref(props.shipZipCode) };
    const isParcelShipping = computed(() => props.productInfo?.ship?.availabilityMessage
      ?.toLowerCase()
      .includes('parcel shipping'));
    const isFurnitureShipping = computed(() => props.productInfo?.ship?.availabilityMessage
      ?.toLowerCase()
      .includes('furniture shipping'));
    const isPickupShown = computed(
      () => !props.isInternationalShip
        && !props.productInfo?.isEGiftCard
        && !isParcelShipping.value
        && !isFurnitureShipping.value,
    );
    const shipEventData = ref(null);

    const sddNddEventData = ref(null);

    const pickupEventData = ref(null);

    // this automatically changes the selected radio button based on when ship or pickup is/becomes unavailable
    const automaticOptionChange = () => {
      const { isAvailableForPickup } = getPickupAvailability(
        props.productInfo?.pickup,
        props.bossEnabled,
      );
      const isAvailableForShipping = props.productInfo?.ship?.available;
      if (isAvailableForShipping) {
        selectedOption.value = 'ShipIt';
      } else if (isAvailableForPickup && isPickupShown.value) {
        selectedOption.value = 'Pickup';
      } else {
        selectedOption.value = '';
      }
    };

    const selectPickupOnStoreChange = () => {
      selectedOption.value = 'Pickup';
    };

    const onUpdateStore = (updatedStoreDetails) => {
      currentStoreDetails.value = updatedStoreDetails;
      emit('current:storeDetails', currentStoreDetails);
    };

    const onUpdateZipcode = (updatedZipCode) => {
      currentZipCode.currentZipCode.value = updatedZipCode;
      emit('current:zipCode', currentZipCode);
    };

    const loadSelectedOption = () => {
      const { isAvailableForPickup } = getPickupAvailability(
        props.productInfo?.pickup,
        props.bossEnabled,
      );
      const isAvailableForShipping = props.productInfo?.ship?.available;
      if (
        isAvailableForShipping
        && props.productInfo.selectedRadioButton !== 'Pickup'
      ) {
        selectedOption.value = 'ShipIt';
      } else if (
        isAvailableForPickup
        && isPickupShown.value
        && (props.productInfo.selectedRadioButton === 'Pickup'
          || !isAvailableForShipping)
      ) {
        selectedOption.value = 'Pickup';
      } else {
        selectedOption.value = '';
      }
    };

    const fetchShipAndPickupBadging = () => {
      if (props.eventBadging && props.eventBadging.events && props.eventBadging.events.length) {
        const event = { ...props.eventBadging.events[0] };

        const shipBadgingData = event.eventAttributes.filter(
          ({ orderType, orderMethod }) => (orderType === 'STH' || orderType === 'EMAIL') && orderMethod === props.productInfo?.ship?.orderType,
        );

        const sddBadgingData = props.sddPromoEnabled ? event.eventAttributes.filter(
          ({ orderType, orderMethod }) => (orderType === 'SDD' || orderType === 'NDD') && orderMethod === props.productInfo?.ship?.orderType,
        ) : [];
        const pickupBadgingData = event.eventAttributes.filter(
          ({ orderType, orderMethod }) => (orderType === 'BOSS' || orderType === 'BOPS') && orderMethod === props.productInfo?.ship?.orderType,
        );

        shipEventData.value = { ...event, eventAttributes: [...shipBadgingData] };
        sddNddEventData.value = { ...event, eventAttributes: [...sddBadgingData] };
        pickupEventData.value = { ...event, eventAttributes: [...pickupBadgingData] };
      } else {
        shipEventData.value = null;
        sddNddEventData.value = null;
        pickupEventData.value = null;
      }
    };

    onMounted(() => {
      loadSelectedOption();

      const { productId, ship } = props.productInfo;
      if (props.page === 'pdp') {
        fireTag('link', {
          event_name: 'product sdd flag',
          pickup_eligibility: [bopsAvailabilityTag(props.pickupStore)],
          product_id: [productId],
          sdd_eligibility_flag: [
            ship.sddAvailability && ship.available ? 'true' : 'false',
          ],
          postalCode: props.shipZipCode,
        });
      }
    });

    watch(selectedOption, (newOption) => {
      emit('selected-option', newOption);
    });

    watch(() => [props.eventBadging, props.productInfo], () => {
      fetchShipAndPickupBadging();
    });

    const toggleLoaderForSlideout = (toggle) => {
      slideOutLoading.value = toggle;
    };
    // this change is temporary for QA test
    // Needs to close this as this is causing the emitter loop.
    // This will only cause the QA not able to see The selected option Change by pasting JSON on run-time.
    // But this will work on mounting.
    // onUpdated(loadSelectedOption);

    return {
      selectedOption,
      isPickupShown,
      isParcelShipping,
      isFurnitureShipping,
      onUpdateStore,
      currentStoreDetails,
      automaticOptionChange,
      selectPickupOnStoreChange,
      onUpdateZipcode,
      slideOutLoading,
      toggleLoaderForSlideout,
      shipEventData,
      pickupEventData,
      sddNddEventData,
      fetchShipAndPickupBadging,
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(.link-button) {
  text-decoration: underline;
  cursor: pointer;
  pointer-events: auto;
}
</style>
